import React, { useState, useRef, useCallback, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import './details.css';
import { ArrowLeft, FileUp, TriangleAlert, X } from 'lucide-react';
import Webcam from 'react-webcam';
import { ChatWarning, HourGlass, Logo } from '../configs';
import { API_ENDPOINTS, get, post, DEPLOYED_APP_URL } from '../configs/api';

export default function LicenseDetail() {
  const { type, pan } = useParams();
  const { state } = useLocation();
  const navigate = useNavigate();

  const isSeed = type === 'seed';
  const label = isSeed ? "Seed" : "Pesticide";
  const editData = state?.editData;
  const initialImages = state?.initialImages;
  const apiResponse = state?.apiResponse;
  const promptUpload = state?.promptUpload;

  // Determine current status: 'rejected', 'in_progress', 'active', or null
  const currentStatus = state?.status || editData?.status;
  const [isEditing, setIsEditing] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [loadingText, setLoadingText] = useState("Submitting, please wait...");

  // Form State
  const [licenseNo, setLicenseNo] = useState(editData?.licenseNo || '');
  const [issueDate, setIssueDate] = useState(editData?.issueDate ? new Date(editData.issueDate) : null);
  const [expiryDate, setExpiryDate] = useState(editData?.expiryDate ? new Date(editData.expiryDate) : null);
  const [reapplicationDate, setReapplicationDate] = useState(
    editData?.reapplicationDate ? new Date(editData.reapplicationDate) : null
  );
  const [noExpiry, setNoExpiry] = useState(editData && !editData.expiryDate);

  // Image handling — auto-open upload prompt when landing from list to add license
  const [images, setImages] = useState(initialImages || editData?.licenseCopyUploaded || []);
  const [reapplicationImages, setReapplicationImages] = useState(initialImages || editData?.reapplicationProofUploaded || []);
  const [activeUploadType, setActiveUploadType] = useState('MAIN'); // 'MAIN' or 'REAPP'
  const [removeType, setRemoveType] = useState('MAIN'); // 'MAIN' or 'REAPP'
  const [showModal, setShowModal] = useState(Boolean(promptUpload));
  const [showWebcam, setShowWebcam] = useState(false);
  const [previewImage, setPreviewImage] = useState(null);
  const [confirmRemoveIndex, setConfirmRemoveIndex] = useState(null);
  const webcamRef = useRef(null);

  const isInProgress = currentStatus === 'under_review';
  const isRejected = currentStatus === 'rejected';
  const isActive = currentStatus === 'approved';
  const isDisabled = isInProgress || ((isRejected || isActive) && !isEditing);

  const isExpiryDateInPast = expiryDate && new Date(expiryDate).setHours(0, 0, 0, 0) < new Date().setHours(0, 0, 0, 0) && !noExpiry;

  useEffect(() => {
    // If expiry date is no longer in the past, or if there's no expiry, clear reapplication date
    if (!isExpiryDateInPast) {
      setReapplicationDate(null);
      setReapplicationImages([]);
    }
  }, [isExpiryDateInPast]);

  // Form is valid if basic info exists and at least one image is present
  const isFormValid = licenseNo && issueDate && (expiryDate || noExpiry) && images.length > 0 && (!isExpiryDateInPast || !!reapplicationDate);

  const dataURLtoFile = (dataurl, filename) => {
    let arr = dataurl.split(','),
      mime = arr[0].match(/:(.*?);/)[1],
      bstr = atob(arr[1]),
      n = bstr.length,
      u8arr = new Uint8Array(n);
    while (n--) {
      u8arr[n] = bstr.charCodeAt(n);
    }
    return new File([u8arr], filename, { type: mime });
  }

  const MAX_DOCUMENTS = 5;

  const handleFileUpload = (e) => {
    const selectedFiles = Array.from(e.target.files);
    const isMain = activeUploadType === 'MAIN';
    const setTarget = isMain ? setImages : setReapplicationImages;
    
    if (selectedFiles.length > 0) {
      setTarget(prev => {
        const remaining = MAX_DOCUMENTS - prev.length;
        if (remaining <= 0) return prev;
        const toAdd = selectedFiles.slice(0, remaining).map(file => ({
          file,
          url: URL.createObjectURL(file),
          isNew: true,
          type: file.type
        }));
        return [...prev, ...toAdd];
      });
      e.target.value = '';
      setShowModal(false);
    }
  };

  const handleCapture = useCallback(() => {
    const imageSrc = webcamRef.current.getScreenshot();
    if (imageSrc) {
      const isMain = activeUploadType === 'MAIN';
      const setTarget = isMain ? setImages : setReapplicationImages;
      setTarget(prev => {
        if (prev.length >= MAX_DOCUMENTS) return prev;
        const file = dataURLtoFile(imageSrc, `webcam-${Date.now()}.jpg`);
        const newImage = {
          file,
          url: URL.createObjectURL(file),
          isNew: true,
          type: 'image/jpeg'
        };
        return [...prev, newImage];
      });
      setShowWebcam(false);
      setShowModal(false);
    }
  }, [webcamRef, activeUploadType]);

  const removeImage = (index, type) => {
    if (type === 'MAIN') {
      setImages(prev => prev.filter((_, i) => i !== index));
    } else {
      setReapplicationImages(prev => prev.filter((_, i) => i !== index));
    }
  };

  const confirmRemove = (index, type) => {
    setConfirmRemoveIndex(index);
    setRemoveType(type);
  };

  const cancelRemove = () => {
    setConfirmRemoveIndex(null);
    setRemoveType('MAIN');
  };

  const proceedRemove = () => {
    if (confirmRemoveIndex === null) return;
    removeImage(confirmRemoveIndex, removeType);
    setConfirmRemoveIndex(null);
  };

  const handleSave = async () => {
    if (isSubmitting) return;
    setIsSubmitting(true);

    try {
      // 1. Separate new images from existing ones
      // Tag objects to distinguish between main license and reapplication proof for docType generation
      const taggedImages = images.map(img => (typeof img === 'object' ? { ...img, isReapplication: false } : img));
      const taggedReappImages = reapplicationImages.map(img => (typeof img === 'object' ? { ...img, isReapplication: true } : img));

      const newImagesToUpload = taggedImages.filter(img => img.isNew);
      const existingImageUrls = taggedImages.filter(img => !img.isNew);

      const newReappImagesToUpload = taggedReappImages.filter(img => img.isNew);
      const existingReappImageUrls = taggedReappImages.filter(img => !img.isNew);

      let uploadedCount = 0;
      const totalUploads = newImagesToUpload.length;

      if (totalUploads > 0) {
        setLoadingText(`Uploading documents 0/${totalUploads}...`);
      } else {
        setLoadingText("Submitting request...");
      }

      // 2. Upload new images using presigned URLs
      const uploadedImageUrls = await Promise.all(
        newImagesToUpload.map(async (image) => {
          const state_code = state?.firmName;
          const sap_id = apiResponse?.sap_ids?.[0];
          const fileName = image.file.name;
          const fileType = fileName.split('.').pop();
          const licenseTypeUpper = type?.toUpperCase() || 'LICENSE';
          const docType = `${licenseTypeUpper}_LICENSE`;

          if (!state_code || !sap_id) {
            throw new Error("State code or SAP ID is missing for presigned URL generation.");
          }

          const presignedUrlEndpoint = `/sap-distributors/license/presigned-url?state_code=${encodeURIComponent(state_code)}&sap_id=${encodeURIComponent(sap_id)}&fileName=${encodeURIComponent(fileName)}&fileType=${encodeURIComponent(fileType)}&docType=${encodeURIComponent(docType)}`;
          const { uploadUrl, fileUrl } = await get(presignedUrlEndpoint);

          // Upload the file to the presigned URL
          await fetch(uploadUrl, {
            method: 'PUT',
            body: image.file,
            headers: {
              'Content-Type': image.file.type,
            },
          });

          uploadedCount++;
          setLoadingText(`Uploading images ${uploadedCount}/${totalUploads}...`);

          console.log(`Successfully uploaded ${image.file.name} to ${fileUrl}`);
          return fileUrl;
        })
      );


       const uploadedReappImageUrls = await Promise.all(
        newReappImagesToUpload.map(async (image) => {
          const state_code = state?.firmName;
          const sap_id = apiResponse?.sap_ids?.[0];
          const fileName = image.file.name;
          const fileType = fileName.split('.').pop();
          const licenseTypeUpper = type?.toUpperCase() || 'LICENSE';
          const docType = `${licenseTypeUpper}_LICENSE_ACK`;
           

          if (!state_code || !sap_id) {
            throw new Error("State code or SAP ID is missing for presigned URL generation.");
          }

          const presignedUrlEndpoint = `/sap-distributors/license/presigned-url?state_code=${encodeURIComponent(state_code)}&sap_id=${encodeURIComponent(sap_id)}&fileName=${encodeURIComponent(fileName)}&fileType=${encodeURIComponent(fileType)}&docType=${encodeURIComponent(docType)}`;
          const { uploadUrl, fileUrl } = await get(presignedUrlEndpoint);

          // Upload the file to the presigned URL
          await fetch(uploadUrl, {
            method: 'PUT',
            body: image.file,
            headers: {
              'Content-Type': image.file.type,
            },
          });

          uploadedCount++;
          setLoadingText(`Uploading images ${uploadedCount}/${totalUploads}...`);

          console.log(`Successfully uploaded ${image.file.name} to ${fileUrl}`);
          return fileUrl;
        })
      );

      setLoadingText("Submitting request...");

      // 3. Combine existing and newly uploaded image URLs
      const allImageUrls = [...existingImageUrls, ...uploadedImageUrls];
      const alReappImageUrls = [...existingReappImageUrls, ...uploadedReappImageUrls];

      // 4. Prepare the data payload for submission
      const formatDate = (date) => {
        if (!date) return null;
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
      };

      const data = {
        document_urls: allImageUrls,
        document_acknowledgement_urls: alReappImageUrls,
        document_license_type: type.toUpperCase(),
        license_number: licenseNo,
        license_issue_date: formatDate(issueDate),
        license_expiry_date: noExpiry ? null : formatDate(expiryDate),
        license_reapplication_date: isExpiryDateInPast && reapplicationDate ? formatDate(reapplicationDate) : null,
      };

      // 5. Get sap_ids and submit the final data to your backend
      const sap_ids = apiResponse?.sap_ids.join(',');
      if (!sap_ids || sap_ids.length === 0) {
        throw new Error("SAP IDs are missing, cannot submit.");
      }

      console.log('Submitting final data to backend:', data);
      await post(API_ENDPOINTS.license_submission(sap_ids), data);
      setIsSubmitting(false);
      navigate(-1); // On success, navigate back. The loader will disappear with the component.
    } catch (error) {
      console.error('Upload or submission failed:', error);
      alert('An error occurred during submission. Please try again.');
      setIsSubmitting(false);
    }
  };

  return (
    <div className="container">
      {isSubmitting && (
        <div className="loader-overlay">
          <div className="loader-spinner" />
          <p className="loader-text">{loadingText}</p>
        </div>
      )}
      <div className="header">
        {/* <ChevronLeft size={24} onClick={() => navigate(-1)} style={{ cursor: 'pointer' }} /> */}
        <img src={Logo} height={40} alt="logo" />
        <h1>Licenses & Certificates</h1>
      </div>


      {/* <div className="info-bar">
        <div className="info-row">
            <span className="info-label">PAN</span>
            <span className="info-value">{pan}</span>
        </div>
        <div className="info-row">
            <span className="info-label">Firm</span>
            <span className="info-value">{state?.firmName}</span>
        </div>
      </div> */}

      {/* --- Status Banners --- */}
      {isInProgress && (
        <div className="status-banner info">
          <img src={HourGlass} height={22} alt="In Progress" />
          <div>
            <h4>We're reviewing your license</h4>
            <p>Thanks for submitting your document. Verification usually takes up to 48 hours.</p>
          </div>
        </div>
      )}

      {isRejected && !isEditing && (
        <div className="status-banner error">
          <img src={ChatWarning} height={22} alt="Rejected" />
          <div>
            <h4>Verification unsuccessful</h4>
            <p>{editData?.rejectedReason || "We couldn't verify the document. Please check the details and resubmit."}</p>
          </div>
        </div>
      )}

      <div className="detail-page-content">
        <div className="dummy-header">
          <button type="button" className="header-back" onClick={() => navigate(-1)} aria-label="Go back">
            <ArrowLeft size={24} />
          </button>
          <h1>{label} License</h1>
        </div>
        <div className="form-container">
          <div className="input-group firstContainer">
            <label>{label} License Document <span className="required-star">*</span></label>
            <p className="info-text">Add a clear photo showing the full document.</p>

            {/* Thumbnails show up here when images exist */}
            {images.length > 0 && (
              <div className="image-grid" style={{ marginBottom: '12px' }}>
                {images.map((img, index) => {
                  const isPdf = (typeof img === 'string' && img.toLowerCase().endsWith('.pdf')) || (img.type === 'application/pdf');
                  const imageUrl = typeof img === 'string' ? `${DEPLOYED_APP_URL}/${img}` : img.url;
                  return (
                    <div key={index} className="image-preview-container">
                      {isPdf ? (
                        <div className="pdf-preview">PDF</div>
                      ) : (
                        <img
                          src={imageUrl}
                          alt="Preview"
                          className="thumb-preview"
                          onClick={() => setPreviewImage(imageUrl)}
                        />
                      )}
                      {!isDisabled && (
                        <button
                          type="button"
                          className="remove-img"
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            confirmRemove(index, 'MAIN');
                          }}
                          aria-label="Remove document"
                        >
                          <span style={{ color: 'white' }}>X</span>
                        </button>
                      )}
                    </div>
                  );
                })}
              </div>
            )}

            {/* Persistent Large Upload Container */}
            {!isDisabled && images.length < MAX_DOCUMENTS && (
              <div className="upload-area" onClick={() => { setActiveUploadType('MAIN'); setShowModal(true); }}>
                <FileUp size={30} className="text-brand-teal" style={{ margin: '0 auto', color: '#007cbf' }} />
                <h5>Click here to upload</h5>
                <p className="document-count-text">{images.length}/{MAX_DOCUMENTS} documents uploaded</p>
              </div>
            )}

            {/* Show count even when upload is hidden (e.g. Under Review, but not Approved) */}
            {((isDisabled && !isActive) || images.length >= MAX_DOCUMENTS) && images.length > 0 && (
              <p className="document-count-text" style={{ textAlign: 'left' }}>{images.length}/{MAX_DOCUMENTS} documents uploaded</p>
            )}
          </div>
          <div className='secondContainer'>
            <div className="input-group">
              <label>{label} License Number <span className="required-star">*</span></label>
              <input
                className="text-input"
                value={licenseNo}
                disabled={isDisabled}
                onChange={(e) => {
                  const value = e.target.value.replace(/^\s+/, '');
                  setLicenseNo(value);
                }}
                onBlur={(e) => setLicenseNo(e.target.value.trim())}
                maxLength={50}
              />
            </div>

            <div className="input-group">
              <label>{label} License Issue Date <span className="required-star">*</span></label>
              <DatePicker
                selected={issueDate}
                maxDate={new Date()}
                showIcon
                onChange={setIssueDate}
                showYearDropdown
                showMonthDropdown
                dropdownMode="select"
                disabled={isDisabled}
                className="date-picker-input"
                dateFormat="dd MMM yyyy"
                icon={
                  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                    <path d="M20 3H19V1H17V3H7V1H5V3H4C2.9 3 2 3.9 2 5V21C2 22.1 2.9 23 4 23H20C21.1 23 22 22.1 22 21V5C22 3.9 21.1 3 20 3ZM20 21H4V8H20V21Z" fill="black" fill-opacity="0.56" />
                  </svg>
                }
              />
            </div>

            <div className="input-group">
              <label>{label} License Expiry Date <span className="required-star">*</span></label>
              {isDisabled && noExpiry ? (
                <input
                  className="text-input"
                  value="No Expiry Date"
                  disabled
                  readOnly
                />
              ) : (
                <DatePicker
                  selected={expiryDate}
                  minDate={issueDate}
                  showIcon
                  onChange={setExpiryDate}
                  showYearDropdown
                  showMonthDropdown
                  dropdownMode="select"
                  disabled={isDisabled || noExpiry}
                  className="date-picker-input"
                  dateFormat="dd MMM yyyy"
                  icon={
                    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                      <path d="M20 3H19V1H17V3H7V1H5V3H4C2.9 3 2 3.9 2 5V21C2 22.1 2.9 23 4 23H20C21.1 23 22 22.1 22 21V5C22 3.9 21.1 3 20 3ZM20 21H4V8H20V21Z" fill="black" fill-opacity="0.56" />
                    </svg>
                  }
                />
              )}
            </div>
            {!isDisabled && (
              <div className="checkbox-group">
                <input type="checkbox" id="no-expiry" checked={noExpiry} onChange={() => setNoExpiry(!noExpiry)} />
                <label htmlFor="no-expiry">No Expiry Date</label>
              </div>
            )}

            {isExpiryDateInPast && (
              <div className="reapplication-block">

                <p className="reapplication-note">
                  <TriangleAlert size={40} color="#EF6D00" />
                  <div className="reapplication-note-text">
                    <span className="reapplication-note-title">Certificate Expired</span>
                    <span className='reapplication-note-desc'>Please provide the reapplication date and proof below.</span>
                  </div>
                </p>


              </div>
            )}


          </div>
        </div>
        {isExpiryDateInPast && (
          <div>

            <div className="dummy-header">

              <h1>Reapplication Details</h1>
            </div>


            <div className="form-container">

              <div className="firstContainer">
                <label>{label} License Reapplication Proof <span className="required-star">*</span></label>
              <p className="info-text" style={{marginBottom:10}}>Add a clear photo showing the full document.</p>
                {/* {reapplicationImages.length > 0 && (
                  <div className="image-grid" style={{ marginBottom: '12px' }}>
                    {reapplicationImages.map((img, index) => {
                      const isPdf = (typeof img === 'string' && img.toLowerCase().endsWith('.pdf')) || (img.type === 'application/pdf');
                      const imageUrl = typeof img === 'string' ? `${DEPLOYED_APP_URL}/${img}` : img.url;
                      return (
                        <div key={index} className="image-preview-container">
                          {isPdf ? <div className="pdf-preview">PDF</div> : <img src={imageUrl} alt="Preview" className="thumb-preview" onClick={() => setPreviewImage(imageUrl)} />}
                          {!isDisabled && (
                            <button
                              type="button"
                              className="remove-img"
                              onClick={(e) => { e.preventDefault(); e.stopPropagation(); confirmRemove(index, 'REAPP'); }}
                            >
                              <span style={{ color: 'white' }}>X</span>
                            </button>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )} */}



            {/* Thumbnails show up here when images exist */}
            {reapplicationImages.length > 0 && (
              <div className="image-grid" style={{ marginBottom: '12px' }}>
                {reapplicationImages.map((img, index) => {
                  const isPdf = (typeof img === 'string' && img.toLowerCase().endsWith('.pdf')) || (img.type === 'application/pdf');
                  const imageUrl = typeof img === 'string' ? `${DEPLOYED_APP_URL}/${img}` : img.url;
                  return (
                    <div key={index} className="image-preview-container">
                      {isPdf ? (
                        <div className="pdf-preview">PDF</div>
                      ) : (
                        <img
                          src={imageUrl}
                          alt="Preview"
                          className="thumb-preview"
                          onClick={() => setPreviewImage(imageUrl)}
                        />
                      )}
                      {!isDisabled && (
                        <button
                          type="button"
                          className="remove-img"
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            confirmRemove(index, 'REAPP');
                          }}
                          aria-label="Remove document"
                        >
                          <span style={{ color: 'white' }}>X</span>
                        </button>
                      )}
                    </div>
                  );
                })}
              </div>
            )}


                {!isDisabled && reapplicationImages.length < MAX_DOCUMENTS && (
                  <div className="upload-area" onClick={() => { setActiveUploadType('REAPP'); setShowModal(true); }}>
                    <FileUp size={30} className="text-brand-teal" style={{ margin: '0 auto', color: '#007cbf' }} />
                    <h5>Click here to upload</h5>
                    <p className="document-count-text">{reapplicationImages.length}/{MAX_DOCUMENTS} documents uploaded</p>
                  </div>
                )}
              </div>


              <div className="secondContainer">
                <div className="input-group">
                  <label>{label} License Reapplication Date <span className="required-star">*</span></label>
                  <DatePicker
                    selected={reapplicationDate}
                    maxDate={new Date()}
                    onChange={setReapplicationDate}
                    showYearDropdown
                    showMonthDropdown
                    dropdownMode="select"
                    showIcon
                    disabled={isDisabled}
                    className="date-picker-input"
                    dateFormat="dd MMM yyyy"
                    icon={
                      <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                        <path d="M20 3H19V1H17V3H7V1H5V3H4C2.9 3 2 3.9 2 5V21C2 22.1 2.9 23 4 23H20C21.1 23 22 22.1 22 21V5C22 3.9 21.1 3 20 3ZM20 21H4V8H20V21Z" fill="black" fill-opacity="0.56" />
                      </svg>
                    }
                  />
                </div>

              </div>
            </div>
          </div>
        )}

        {/* Action buttons — sticky bar on mobile, inline on desktop */}
        <div className="action-bar">
          {isActive ? (
            null
          ) : (isRejected && !isEditing) ? (
            <button
              type="button"
              className="save-btn active"
              onClick={() => setIsEditing(true)}
            >
              Edit & Resubmit
            </button>
          ) : isInProgress ? null : (
            <button
              type="button"
              className={`save-btn ${isFormValid ? 'active' : ''}`}
              disabled={!isFormValid}
              onClick={handleSave}
            >
              Save
            </button>
          )}
        </div>
      </div>




      {showModal && (
        <div className="upload-modal-overlay" onClick={() => setShowModal(false)}>
          <div className="upload-modal-content" onClick={(e) => e.stopPropagation()}>
            <button type="button" className="upload-modal-close" onClick={() => setShowModal(false)} aria-label="Close">
              &times;
            </button>
            <h2 className="upload-modal-title">Upload {label} license photo</h2>
            <p className="upload-modal-hint">Add a clear photo showing the full document</p>
            <div className="upload-modal-actions">
              <button
                type="button"
                className="upload-modal-btn upload-modal-btn-primary upload-modal-btn-camera"
                onClick={() => { setShowModal(false); setShowWebcam(true); }}
              >
                Use Camera
              </button>
              <label className="upload-modal-btn upload-modal-btn-secondary">
                Upload from Gallery
                <input type="file" accept="image/*,application/pdf" multiple hidden onChange={handleFileUpload} />
              </label>
            </div>
          </div>
        </div>
      )}

      {showWebcam && (
        <div className="modal-overlay" style={{ backgroundColor: 'black' }}>
          <Webcam
            audio={false}
            ref={webcamRef}
            screenshotFormat="image/jpeg"
            style={{ width: '100%', height: '100%', objectFit: 'cover' }}
            videoConstraints={{
              width: 1920,
              height: 1080,
              facingMode: "environment"
            }}
          />
          <div className="webcam-actions">
            <button type="button" className="webcam-btn webcam-btn-cancel" onClick={() => setShowWebcam(false)}>Cancel</button>
            <button type="button" className="webcam-btn webcam-btn-capture" onClick={handleCapture}>Capture</button>
          </div>
        </div>
      )}

      {previewImage && (
        <div className="modal-overlay image-preview-overlay" onClick={() => setPreviewImage(null)}>
          <div className="image-preview-modal" onClick={(e) => e.stopPropagation()}>
            <button
              type="button"
              className="image-preview-close"
              onClick={() => setPreviewImage(null)}
              aria-label="Close preview"
            >
              <X size={20} />
            </button>
            <img src={previewImage} alt="Full preview" className="image-preview-full" />
          </div>
        </div>
      )}

      {confirmRemoveIndex !== null && (
        <div className="confirm-modal-overlay" onClick={cancelRemove}>
          <div className="confirm-modal-content" onClick={(e) => e.stopPropagation()}>
            <h2 className="confirm-modal-title">Remove document?</h2>
            <p className="confirm-modal-hint">Are you sure you want to remove this document from your upload?</p>
            <div className="confirm-modal-actions">
              <button
                type="button"
                className="confirm-modal-btn confirm-modal-btn-secondary"
                onClick={cancelRemove}
              >
                No
              </button>
              <button
                type="button"
                className="confirm-modal-btn confirm-modal-btn-primary"
                onClick={proceedRemove}
              >
                Yes, remove
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
